import { initPylon } from '@findigs/helpers/src/integrations/pylon/pylon';
import {
  NavLink as RemixNavLink,
  useLocation,
  useMatches,
} from '@remix-run/react';
import { useEffect, useMemo } from 'react';
import type { NavbarPureProps } from '~/components/navbar/navbar-pure';
import { NavbarPure } from '~/components/navbar/navbar-pure';
import { useBackButtonDestination } from '~/hooks/use-back-button-destination';
import type { LoaderResult } from './navbar.server';
import PerformanceIcon from '@findigs/ui-kit/src/icons/material/bar_chart.inline.svg';
import ConfigurationIcon from '@findigs/ui-kit/src/icons/material/build.filled.inline.svg';
import SecurityDepositIcon from '@findigs/ui-kit/src/icons/material/credit_score.inline.svg';
import FactCheck from '@findigs/ui-kit/src/icons/material/fact_check.inline.svg';
import HousingIcon from '@findigs/ui-kit/src/icons/material/home.filled.inline.svg';
import UnitsIcon from '@findigs/ui-kit/src/icons/material/maps_home_work.filled.inline.svg';
import RevenueShareIcon from '@findigs/ui-kit/src/icons/material/monetization_on.filled.inline.svg';
import ProspectsIcon from '@findigs/ui-kit/src/icons/material/people.filled.inline.svg';
import TeamMembersIcon from '@findigs/ui-kit/src/icons/material/person.filled.inline.svg';
import PromoCodeIcon from '@findigs/ui-kit/src/icons/material/promo-codes.inline.svg';
import ApplicationsIcon from '@findigs/ui-kit/src/icons/material/text_snippet.filled.inline.svg';
import SupportIcon from '@findigs/ui-kit/src/icons/support-flag.inline.svg';

const primaryLinks: NavbarPureProps<any>['primaryLinks'] = [
  {
    title: 'Performance',
    href: '/performance-analytics',
    icon: <PerformanceIcon width="20" height="20" aria-hidden={true} />,
    active: false,
    isInternal: true,
  },
  {
    title: 'Prospects',
    href: '/prospects',
    icon: <ProspectsIcon width="20" height="20" aria-hidden={true} />,
    active: false,
  },
  {
    title: 'Applications',
    href: '/applications',
    icon: <ApplicationsIcon width="20" height="20" aria-hidden={true} />,
    active: false,
  },
  {
    title: 'Deposits',
    href: '/security-deposit',
    icon: <SecurityDepositIcon width="20" height="20" aria-hidden={true} />,
    active: false,
    isInternal: true,
  },
  {
    title: 'Units',
    href: '/units-new',
    icon: <UnitsIcon width="20" height="20" aria-hidden={true} />,
    active: false,
  },
  {
    title: 'Revenue share',
    href: '/revenue-share',
    icon: <RevenueShareIcon width="20" height="20" aria-hidden={true} />,
    active: false,
  },
  {
    title: 'Configuration',
    href: '/config',
    icon: <ConfigurationIcon width="20" height="20" aria-hidden={true} />,
    active: false,
  },
  {
    title: 'Team members',
    href: '/access',
    icon: <TeamMembersIcon width="20" height="20" aria-hidden={true} />,
    active: false,
    isInternal: true,
  },
  {
    title: 'Screening criteria',
    href: '/screening-criteria',
    icon: <FactCheck width="20" height="20" aria-hidden={true} />,
    active: false,
    isInternal: true,
  },
  {
    title: 'Housing history',
    href: '/housing-history',
    icon: <HousingIcon width="20" height="20" aria-hidden={true} />,
    active: false,
    isInternal: true,
  },
  {
    title: 'Support',
    href: '/support',
    icon: <SupportIcon width="20" height="20" aria-hidden={true} />,
    active: false,
    isInternal: true,
  },
  {
    title: 'Promo codes',
    href: '/promo-management',
    icon: <PromoCodeIcon width="20" height="20" aria-hidden={true} />,
    active: false,
    isInternal: true,
  },
];

const getSupportLink = {
  title: 'Get support',
  onClick: () => {
    if (window.Pylon) {
      window.Pylon('show');
    }
  },
  active: false,
};

const logoutHref = '/logout';

export type NavbarProps = {
  loaderData: LoaderResult;
} & Pick<NavbarPureProps<any>, 'logoHref' | 'variant'>;

const getUrlPathName = (url: string) => {
  const urlObj = new URL((url.startsWith('/') ? 'http://a' : '') + url);
  return urlObj.pathname;
};

export const Navbar = ({ loaderData, logoHref, variant }: NavbarProps) => {
  const { backButtonDestination } = useBackButtonDestination();

  const location = useLocation();
  const matches = useMatches();

  useEffect(() => {
    if (loaderData && loaderData.profile && !loaderData.profile.isStaff) {
      initPylon(loaderData.PYLON_CHAT_ENV, loaderData.profile);
    }
  }, [loaderData, location.pathname]);

  // The in-app Pylon chat is only initialized for non-staff users (see above).
  // Internal users — including underwriters, who use the standalone Pylon
  // portal — should not see the trigger that calls window.Pylon('show').
  const secondaryLinks = useMemo<NavbarPureProps<any>['secondaryLinks']>(
    () => (loaderData?.profile?.isStaff ? [] : [getSupportLink]),
    [loaderData?.profile?.isStaff]
  );

  const enhancedPrimaryLinks = useMemo(() => {
    if (!loaderData?.profile) {
      return [];
    }

    return primaryLinks
      .filter(
        (primaryLink) =>
          primaryLink.href &&
          loaderData?.navItemVisibilityToggleMap[primaryLink.href] !== false
      )
      .map((link) => {
        const customization =
          (link.href && loaderData?.navItemCustomizationByHrefMap[link.href]) ??
          {};
        const hrefOverride = link.href
          ? loaderData?.navItemHrefOverrideMap?.[link.href] ?? link.href
          : link.href;
        const active =
          (hrefOverride?.startsWith('/') &&
            matches.some(
              (match) => match.pathname === getUrlPathName(hrefOverride)
            )) ??
          false;
        return {
          ...link,
          ...customization,
          active,
          href: hrefOverride,
        };
      });
  }, [loaderData, matches]);

  return (
    <NavbarPure
      primaryLinks={enhancedPrimaryLinks}
      secondaryLinks={secondaryLinks}
      backButtonDestination={backButtonDestination}
      logoHref={logoHref}
      logoutHref={logoutHref}
      frameworkLink={RemixNavLink}
      frameworkLinkProp="to"
      profile={loaderData?.profile}
      variant={variant}
    />
  );
};
